import { types } from 'mobx-state-tree';

import { Address, partiallyUpdateModel, PROVINCES } from '../definitions/Common';
import { ContactTimes } from '../definitions/InsCommon';
import UserPurposeValues from '../definitions/UserPurposeValues';
import Driver from './Driver';
import Vehicle from './Vehicle';


const Application = types.model('Application', {
    id: types.identifier,

    email: types.maybeNull(types.string),
    phone: types.maybeNull(types.string),
    address: types.maybeNull(Address),
    contactTime: types.optional(
        types.enumeration(Object.values(ContactTimes)),
        ContactTimes.ANYTIME,
    ),
    userPurpose: types.optional(
        types.enumeration(Object.values(UserPurposeValues)),
        UserPurposeValues.NEW_CAR,
    ),

    creditCheckConsent: types.maybeNull(types.boolean),
    newsletterSignupConsent: types.maybeNull(types.boolean),
    wantsToBundleInsurance: types.maybeNull(types.boolean),
    wantsToUseTelematics: types.maybeNull(types.boolean),

    vehicles: types.optional(types.array(Vehicle), []),
    drivers: types.optional(types.array(Driver), []),
}).views(self => ({
    get isValid() {
        return self.areAssignmentsValid
            && self.areUserDetailsValid
            && self.areDriversValid
            && self.areVehiclesValid;
    },
    get areAssignmentsValid() {
        return self.vehicles.every(v => v.isAssignmentValid);
    },
    get areUserDetailsValid() {
        return (
            self.email !== null
            && self.newsletterSignupConsent !== null
            && self.phone !== null
            && self.wantsToBundleInsurance !== null
            && self.address !== null
            && self.wantsToUseTelematics !== null
            && (
                self.province === PROVINCES.ALBERTA
                    ? self.creditCheckConsent !== null // if Alberta, validate creditCheckConsent
                    : true // otherwise ignore this validation
            )
        );
    },
    get areDriversValid() {
        return self.drivers.length > 0 && self.drivers.every(driver => driver.isValid);
    },
    get areVehiclesValid() {
        return self.vehicles.length > 0;
    },
    get primaryApplicant() {
        return self.drivers.length > 0 ? self.drivers[0] : null;
    },
})).actions((self) => ({
    update(values) {
        partiallyUpdateModel(self, values);
    },
    updateUserDetails({
        email,
        newsletterSignupConsent,
        phone,
        wantsToBundleInsurance,
        address,
        wantsToUseTelematics,
        creditCheckConsent,
    }) {
        self.primaryApplicant?.update({ email, phone });
        self.update({
            email,
            newsletterSignupConsent,
            phone,
            wantsToBundleInsurance,
            address,
            wantsToUseTelematics,
            creditCheckConsent,
        });
    },
    setUserLanguage(language) {
        self.primaryApplicant?.update({ language });
    },
    _autoAssignPrimaryDrivers() {
        // REQUIREMENT: if we're the ONLY driver, auto-assign us to vehicles.
        if (self.drivers.length === 1) {
            const driver = self.drivers[0];
            self.vehicles.forEach(vehicle => {
                vehicle.setPrimaryDriver(driver.id);
            });
        }
    },
    addVehicle(vehicle) {
        if (vehicle?.id == null) {
            throw new Error('[addVehicle] snapshot or id is missing, id should be given to sync with vehicleEditor.id');
        }
    
        const createdVehicle = Vehicle.create(vehicle);

        // REQUIREMENT: if we have exactly 1 driver defined, assume it's our primary.
        if (self.drivers.length === 1) {
            createdVehicle.setPrimaryDriver(self.drivers[0].id);
        }

        self.vehicles.push(createdVehicle);
    },
    removeVehicle(vehicleId) {
        self.vehicles.remove(vehicleId);
    },
    removeDriver(driver) {
        // They can no longer be assigned to a vehicle.
        self.vehicles.forEach(vehicle => {
            if (vehicle.primaryDriverId === driver.id) {
                vehicle.setPrimaryDriver(null);
            }
        });

        self.drivers.remove(driver);
        self._autoAssignPrimaryDrivers();
    },
    addDriver(driver) {
        // REQUIREMENT: clear our existing assignments when adding a new driver.
        self.vehicles.forEach(vehicle => {
            vehicle.setPrimaryDriver(null);
        });

        self.drivers.push(Driver.create(driver));
        self._autoAssignPrimaryDrivers();
    },
}));

export default Application;
